import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, Plane, X, Upload, Trash, Eye, Users, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { formatBRL, statusInfo } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';

const EMPTY = {
  nome: '', destino: '', supplier_id: '', data_inicio: '', data_fim: '', preco_venda: 0,
  quantidade_total: 0, custos: [], status: 'planejada', publicada: false, exibir_valor: true, descricao_publica: '', fotos: [],
};

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const filesToBase64 = (files) => Promise.all(Array.from(files).map((f) => new Promise((res) => {
  const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f);
})));

export default function Trips() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isManager = ['admin', 'supervisor'].includes(user?.role);
  const readOnly = !isManager;
  const [trips, setTrips] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  // Passengers modal (managers only)
  const [paxOpen, setPaxOpen] = useState(false);
  const [paxTrip, setPaxTrip] = useState(null);
  const [paxData, setPaxData] = useState(null);
  const [paxLoading, setPaxLoading] = useState(false);
  const [paxExporting, setPaxExporting] = useState(false);

  const downloadBlob = (data, filename, type) => {
    const blob = new Blob([data], { type });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openPassengers = async (t) => {
    setPaxTrip(t); setPaxOpen(true); setPaxData(null); setPaxLoading(true);
    try { const r = await api.get(`/trips/${t.trip_id}/passengers`); setPaxData(r.data); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao carregar passageiros'); }
    finally { setPaxLoading(false); }
  };

  const exportPassengers = async () => {
    if (!paxTrip) return;
    setPaxExporting(true);
    try {
      const r = await api.get(`/trips/${paxTrip.trip_id}/passengers/export`, { responseType: 'blob' });
      const safe = (paxTrip.nome || 'viagem').replace(/[^a-zA-Z0-9 _-]/g, '').trim().replace(/\s+/g, '_') || 'viagem';
      downloadBlob(r.data, `passageiros_${safe}.xlsx`, XLSX_MIME);
      toast.success('Manifesto exportado');
    } catch { toast.error('Erro ao exportar passageiros'); }
    finally { setPaxExporting(false); }
  };

  const load = () => {
    Promise.all([api.get('/trips'), api.get('/suppliers')])
      .then(([t, s]) => { setTrips(t.data); setSuppliers(s.data); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Pré-preenchimento vindo da Calculadora de Precificação
  useEffect(() => {
    const prefill = location.state?.prefill;
    if (prefill && isManager) {
      setEditing(null);
      setForm({ ...EMPTY, ...prefill, custos: prefill.custos || [], fotos: [] });
      setOpen(true);
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const selectedSupplier = suppliers.find((s) => s.supplier_id === form.supplier_id);
  const isPropria = selectedSupplier?.tipo === 'propria';
  const custoTotal = (form.custos || []).reduce((a, c) => a + Number(c.valor || 0), 0);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ ...EMPTY, ...t, custos: t.custos || [], fotos: t.fotos || [] }); setOpen(true); };

  const addCost = () => setForm({ ...form, custos: [...form.custos, { descricao: '', valor: 0, fornecedor: '' }] });
  const updateCost = (i, k, v) => { const c = [...form.custos]; c[i] = { ...c[i], [k]: v }; setForm({ ...form, custos: c }); };
  const removeCost = (i) => setForm({ ...form, custos: form.custos.filter((_, idx) => idx !== i) });

  const onUpload = async (e) => {
    const files = e.target.files; if (!files?.length) return;
    const b64 = await filesToBase64(files);
    setForm((f) => ({ ...f, fotos: [...f.fotos, ...b64] }));
    e.target.value = '';
  };
  const removePhoto = (i) => setForm({ ...form, fotos: form.fotos.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    if (!form.nome || !form.destino) { toast.error('Nome e destino são obrigatórios'); return; }
    setSaving(true);
    const payload = {
      ...form,
      preco_venda: Number(form.preco_venda || 0),
      quantidade_total: Number(form.quantidade_total || 0),
      custos: form.custos.map((c) => ({ descricao: c.descricao, valor: Number(c.valor || 0), fornecedor: c.fornecedor || '' })),
      supplier_id: form.supplier_id || null,
    };
    try {
      if (editing) { await api.put(`/trips/${editing.trip_id}`, payload); toast.success('Viagem atualizada'); }
      else { await api.post('/trips', payload); toast.success('Viagem cadastrada'); }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`/trips/${delTarget.trip_id}`); toast.success('Viagem removida'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao remover'); }
    finally { setDelTarget(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Viagens</h2>
          <p className="text-sm text-muted-foreground">Cadastro de viagens, custos por fornecedor e publicação na galeria.</p>
        </div>
        {isManager && <Button onClick={openNew} data-testid="trips-new-button"><Plus className="h-4 w-4 mr-2" /> Nova viagem</Button>}
      </div>

      <Card className="p-4">
        {loading ? <p className="text-muted-foreground py-8 text-center">Carregando...</p> : trips.length === 0 ? (
          <div className="py-12 text-center"><Plane className="h-8 w-8 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Nenhuma viagem cadastrada.</p></div>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="trips-table">
              <TableHeader><TableRow>
                <TableHead>Viagem</TableHead><TableHead>Fornecedor</TableHead><TableHead>Status</TableHead>
                <TableHead className="text-center">Vagas</TableHead>
                <TableHead className="text-right">Custo</TableHead><TableHead className="text-right">Receita</TableHead>
                <TableHead className="text-right">Lucro</TableHead><TableHead>Galeria</TableHead><TableHead className="w-10"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {trips.map((t) => {
                  const si = statusInfo(t.status);
                  return (
                    <TableRow key={t.trip_id}>
                      <TableCell><div className="font-medium">{t.nome}</div><div className="text-xs text-muted-foreground">{t.destino}</div></TableCell>
                      <TableCell className="text-muted-foreground">{t.supplier_nome || '-'}</TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${si.cls}`}>{si.label}</span></TableCell>
                      <TableCell className="text-center">
                        {t.vagas_restantes == null ? (
                          <span className="text-xs text-muted-foreground" data-testid={`trip-vagas-${t.trip_id}`}>—</span>
                        ) : (
                          <span
                            className={`text-xs font-medium tabular-nums px-2 py-0.5 rounded-full ${t.vagas_restantes <= 0 ? 'bg-destructive/10 text-destructive' : 'bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)]'}`}
                            data-testid={`trip-vagas-${t.trip_id}`}
                          >
                            {t.vagas_restantes}/{t.quantidade_total}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatBRL(t.custo_total)}</TableCell>
                      <TableCell className="text-right tabular-nums">{formatBRL(t.receita)}</TableCell>
                      <TableCell className={`text-right tabular-nums ${t.lucro >= 0 ? '' : 'text-destructive'}`}>{formatBRL(t.lucro)}</TableCell>
                      <TableCell>{t.publicada ? <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)]">Publicada</Badge> : <Badge variant="secondary">Privada</Badge>}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`trips-row-actions-${t.trip_id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(t)}>
                              {isManager ? <><Pencil className="h-4 w-4 mr-2" /> Editar</> : <><Eye className="h-4 w-4 mr-2" /> Ver detalhes</>}
                            </DropdownMenuItem>
                            {isManager && <DropdownMenuItem onClick={() => openPassengers(t)} data-testid={`trips-view-passengers-${t.trip_id}`}><Users className="h-4 w-4 mr-2" /> Ver passageiros</DropdownMenuItem>}
                            {isManager && <DropdownMenuItem onClick={() => setDelTarget(t)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{readOnly ? 'Detalhes da viagem' : (editing ? 'Editar viagem' : 'Nova viagem')}</DialogTitle></DialogHeader>
          <form onSubmit={save}>
            <fieldset disabled={readOnly} className="contents">
            <Tabs defaultValue="geral">
              <TabsList className="grid grid-cols-3 w-full">
                <TabsTrigger value="geral" data-testid="trips-tab-geral">Geral</TabsTrigger>
                <TabsTrigger value="custos" data-testid="trips-tab-custos">Custos</TabsTrigger>
                <TabsTrigger value="fotos" data-testid="trips-tab-fotos">Fotos</TabsTrigger>
              </TabsList>

              <TabsContent value="geral" className="space-y-4 pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2"><Label>Nome da viagem *</Label><Input value={form.nome} onChange={set('nome')} className="mt-1.5" data-testid="trip-nome" /></div>
                  <div className="sm:col-span-2"><Label>Destino *</Label><Input value={form.destino} onChange={set('destino')} placeholder="Ex: Porto de Galinhas - PE" className="mt-1.5" data-testid="trip-destino" /></div>
                  <div><Label>Fornecedor</Label>
                    <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="trip-supplier"><SelectValue placeholder="Selecione" /></SelectTrigger>
                      <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.nome}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Status</Label>
                    <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                      <SelectTrigger className="mt-1.5" data-testid="trip-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="planejada">Planejada</SelectItem><SelectItem value="confirmada">Confirmada</SelectItem>
                        <SelectItem value="executada">Executada</SelectItem><SelectItem value="cancelada">Cancelada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Data início</Label><Input type="date" value={form.data_inicio || ''} onChange={set('data_inicio')} className="mt-1.5" /></div>
                  <div><Label>Data fim</Label><Input type="date" value={form.data_fim || ''} onChange={set('data_fim')} className="mt-1.5" /></div>
                  <div><Label>Preço de venda (R$)</Label><Input type="number" step="0.01" value={form.preco_venda} onChange={set('preco_venda')} className="mt-1.5" data-testid="trip-preco" /></div>
                  {(isPropria || Number(form.quantidade_total) > 0) && <div><Label>Quantidade total (vagas)</Label><Input type="number" value={form.quantidade_total} onChange={set('quantidade_total')} className="mt-1.5" data-testid="trips-form-total-quantity" /></div>}
                </div>
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div><Label>Publicar na galeria pública</Label><p className="text-xs text-muted-foreground mt-1">Visível no site. Aparece em "Já realizadas" se o status for Executada, ou em "Próximas viagens" se for Planejada/Confirmada.</p></div>
                  <Switch checked={form.publicada} onCheckedChange={(v) => setForm({ ...form, publicada: v })} data-testid="trip-publicar" />
                </div>
                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div><Label>Exibir valor no site</Label><p className="text-xs text-muted-foreground mt-1">Mostra o preço de venda na galeria pública.</p></div>
                  <Switch checked={form.exibir_valor} onCheckedChange={(v) => setForm({ ...form, exibir_valor: v })} data-testid="trip-exibir-valor" />
                </div>
                <div><Label>Descrição pública</Label><Textarea value={form.descricao_publica} onChange={set('descricao_publica')} rows={2} className="mt-1.5" /></div>
              </TabsContent>

              <TabsContent value="custos" className="space-y-3 pt-4">
                {form.custos.length === 0 && <p className="text-sm text-muted-foreground">Adicione itens de custo (hospedagem, aéreo, traslados...).</p>}
                {form.custos.map((c, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5"><Label className="text-xs">Descrição</Label><Input value={c.descricao} onChange={(e) => updateCost(i, 'descricao', e.target.value)} className="mt-1" /></div>
                    <div className="col-span-4"><Label className="text-xs">Fornecedor</Label><Input value={c.fornecedor} onChange={(e) => updateCost(i, 'fornecedor', e.target.value)} className="mt-1" /></div>
                    <div className="col-span-2"><Label className="text-xs">Valor</Label><Input type="number" step="0.01" value={c.valor} onChange={(e) => updateCost(i, 'valor', e.target.value)} className="mt-1" /></div>
                    <div className="col-span-1"><Button type="button" variant="ghost" size="icon" onClick={() => removeCost(i)}><Trash className="h-4 w-4 text-destructive" /></Button></div>
                  </div>
                ))}
                <Button type="button" variant="secondary" onClick={addCost} data-testid="trip-add-cost"><Plus className="h-4 w-4 mr-2" /> Adicionar custo</Button>
                <div className="flex justify-between items-center pt-3 border-t mt-3"><span className="text-sm text-muted-foreground">Custo total</span><span className="font-display text-xl font-semibold tabular-nums">{formatBRL(custoTotal)}</span></div>
              </TabsContent>

              <TabsContent value="fotos" className="space-y-4 pt-4">
                <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border p-8 cursor-pointer hover:bg-accent/40">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Clique para enviar fotos</span>
                  <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} data-testid="trips-photos-upload" />
                </label>
                {form.fotos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                    {form.fotos.map((f, i) => (
                      <div key={i} className="relative group">
                        <img src={f} alt={`foto ${i + 1}`} className="h-24 w-full object-cover rounded-lg" />
                        <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
            </fieldset>
            <DialogFooter className="mt-4"><Button type="button" variant="ghost" onClick={() => setOpen(false)}>{readOnly ? 'Fechar' : 'Cancelar'}</Button>{!readOnly && <Button type="submit" disabled={saving} data-testid="trip-save">{saving ? 'Salvando...' : 'Salvar viagem'}</Button>}</DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={paxOpen} onOpenChange={setPaxOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="trip-passengers-dialog">
          <DialogHeader>
            <DialogTitle>Passageiros — {paxTrip?.nome}</DialogTitle>
          </DialogHeader>

          {paxLoading ? (
            <p className="text-muted-foreground py-10 text-center">Carregando passageiros...</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Passageiros</p>
                  <p className="font-display text-2xl font-semibold tabular-nums" data-testid="pax-count">{paxData?.passageiros_count ?? 0}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Vagas totais</p>
                  <p className="font-display text-2xl font-semibold tabular-nums">{paxData?.quantidade_total ? paxData.quantidade_total : '—'}</p>
                </div>
                <div className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">Vagas restantes</p>
                  <p className={`font-display text-2xl font-semibold tabular-nums ${paxData?.vagas_restantes != null && paxData.vagas_restantes <= 0 ? 'text-destructive' : ''}`} data-testid="pax-vagas-restantes">
                    {paxData?.vagas_restantes == null ? '—' : paxData.vagas_restantes}
                  </p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button variant="secondary" onClick={exportPassengers} disabled={paxExporting || !(paxData?.passageiros_count)} data-testid="pax-export-button">
                  <Download className="h-4 w-4 mr-2" /> {paxExporting ? 'Exportando...' : 'Exportar XLSX'}
                </Button>
              </div>

              {(!paxData?.passageiros || paxData.passageiros.length === 0) ? (
                <div className="py-12 text-center"><Users className="h-8 w-8 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Nenhum passageiro vinculado a esta viagem.</p></div>
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <Table data-testid="pax-table">
                    <TableHeader><TableRow>
                      <TableHead>Nome</TableHead><TableHead>Nascimento</TableHead><TableHead>CPF</TableHead>
                      <TableHead>RG</TableHead><TableHead>E-mail</TableHead><TableHead>Telefone</TableHead>
                      <TableHead>Tipo sang.</TableHead><TableHead>Alergias</TableHead><TableHead>Remédios</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {paxData.passageiros.map((p) => (
                        <TableRow key={p.sale_id} data-testid={`pax-row-${p.client_id}`}>
                          <TableCell className="font-medium">{p.nome}</TableCell>
                          <TableCell className="text-muted-foreground">{p.data_nascimento || '-'}</TableCell>
                          <TableCell className="text-muted-foreground">{p.cpf || '-'}</TableCell>
                          <TableCell className="text-muted-foreground">{p.rg || '-'}</TableCell>
                          <TableCell className="text-muted-foreground">{p.email || '-'}</TableCell>
                          <TableCell className="text-muted-foreground">{p.telefone || '-'}</TableCell>
                          <TableCell className="text-muted-foreground">{p.tipo_sanguineo || '-'}</TableCell>
                          <TableCell className="text-muted-foreground max-w-[160px] truncate" title={p.alergias}>{p.alergias || '-'}</TableCell>
                          <TableCell className="text-muted-foreground max-w-[160px] truncate" title={p.remedios_continuos}>{p.remedios_continuos || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button type="button" variant="ghost" onClick={() => setPaxOpen(false)}>Fechar</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir viagem?</AlertDialogTitle><AlertDialogDescription>Deseja excluir {delTarget?.nome}? Lançamentos automáticos relacionados serão removidos.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
